import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Popover } from "@mui/material";
import { TbCalendar, TbChevronDown } from "react-icons/tb";
import { DateRangeCalendar } from "../../../../../Components/Molecules/GridHistory";
import { formatFilterDate } from "../../../../../Components/Molecules/GridHistory/gridFilterUtils";
import { PanelActions } from "../../../../../Components/Molecules/Filters/ModernFilterPanels";
import {
  PROCESSING_VOLUME_PRESETS,
  detectProcessingDatePreset,
  getProcessingPresetDateRange,
  normalizeProcessingDateRange,
} from "../baasHomeProcessingDateFilters";

const PRESET_OPTIONS = [
  { id: PROCESSING_VOLUME_PRESETS.LAST_24H, labelKey: "baas.api.period.24h" },
  { id: PROCESSING_VOLUME_PRESETS.LAST_7D, labelKey: "baas.api.period.7d" },
  { id: PROCESSING_VOLUME_PRESETS.LAST_30D, labelKey: "baas.api.period.30d" },
];

const getRangeSummary = (dateRange) => {
  const { dateFrom, dateTo, preset } = normalizeProcessingDateRange(dateRange);

  if (preset !== PROCESSING_VOLUME_PRESETS.CUSTOM) {
    return intl.get("extract.periodCustom");
  }

  if (dateFrom && dateTo) {
    return `${formatFilterDate(dateFrom)} — ${formatFilterDate(dateTo)}`;
  }

  return intl.get("extract.periodCustom");
};

export const BaasHomeProcessingDateFilter = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const activePreset = useMemo(
    () => normalizeProcessingDateRange(value).preset,
    [value],
  );

  const closePanel = () => setAnchorEl(null);

  const applyPreset = (preset) => {
    onChange(getProcessingPresetDateRange(preset));
  };

  const openCustomPanel = (event) => {
    setDraft(value);
    setAnchorEl(event.currentTarget);
  };

  const applyCustomRange = () => {
    const dateFrom = draft.dateFrom ? startOfDaySafe(draft.dateFrom) : null;
    const dateTo = draft.dateTo ? startOfDaySafe(draft.dateTo) : null;

    if (!dateFrom || !dateTo) {
      closePanel();
      return;
    }

    onChange({
      dateFrom,
      dateTo,
      preset: detectProcessingDatePreset(dateFrom, dateTo),
    });
    closePanel();
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {intl.get("baas.home.dashboard.processingRails.volumeFilter")}
      </span>

      <div
        className="extract-date-range-calendar__presets"
        role="group"
        aria-label={intl.get("baas.home.dashboard.processingRails.volumeFilter")}
      >
        {PRESET_OPTIONS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`extract-date-range-calendar__preset-btn ${
              activePreset === preset.id
                ? "extract-date-range-calendar__preset-btn--active"
                : ""
            }`.trim()}
            onClick={() => applyPreset(preset.id)}
          >
            {intl.get(preset.labelKey)}
          </button>
        ))}

        <button
          type="button"
          className={`extract-date-range-calendar__preset-btn inline-flex items-center gap-1 ${
            activePreset === PROCESSING_VOLUME_PRESETS.CUSTOM
              ? "extract-date-range-calendar__preset-btn--active"
              : ""
          }`.trim()}
          onClick={openCustomPanel}
          aria-expanded={Boolean(anchorEl)}
        >
          <TbCalendar className="h-3 w-3 shrink-0" aria-hidden />
          <span className="whitespace-nowrap">{getRangeSummary(value)}</span>
          <TbChevronDown className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
        </button>
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePanel}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            className: "extract-filter-panel",
          },
        }}
      >
        <div className="extract-filter-panel__header">
          <p className="extract-filter-panel__title">
            {intl.get("baas.home.dashboard.processingRails.volumeFilter")}
          </p>
        </div>
        <DateRangeCalendar
          dateFrom={draft.dateFrom}
          dateTo={draft.dateTo}
          onChange={({ dateFrom, dateTo }) =>
            setDraft((current) => ({
              ...current,
              dateFrom,
              dateTo,
              preset: PROCESSING_VOLUME_PRESETS.CUSTOM,
            }))
          }
        />
        <PanelActions onCancel={closePanel} onApply={applyCustomRange} />
      </Popover>
    </div>
  );
};

const startOfDaySafe = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};
